import * as React from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

// The zone the picker works in is the viewer's own (the browser takes it from the
// OS). Name it only when it's a real region, e.g. "Central European Time"; a bare
// offset or UTC means the location isn't known, so nothing is shown rather than a
// guess. Same rule as the iOS app.
export const timeZoneLabel = (
  zone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  locale = undefined,
  at = new Date()
) => {
  try {
    if (!zone || !zone.includes("/") || zone.startsWith("Etc/")) return null;
    const name = new Intl.DateTimeFormat(locale, { timeZone: zone, timeZoneName: "longGeneric" })
      .formatToParts(at)
      .find((part) => part.type === "timeZoneName")?.value;
    if (!name || name.startsWith("GMT") || name.startsWith("UTC")) return null;
    return name;
  } catch (_error) {
    return null;
  }
};

/** Next full hour from now — the default embargo, same as the iOS app. */
export const nextFullHour = (now = new Date()) => {
  const next = new Date(now);
  next.setMinutes(0, 0, 0);
  next.setHours(next.getHours() + 1);
  return next;
};

/**
 * Date + time picker for the embargo (24h, nothing before now). Pairs with the
 * iOS app's "Embargo until" row: the embargo lifts at the exact minute picked,
 * in the uploader's time zone.
 */
export default function EmbargoDateTimePicker({ label, value, onChange, error, helperText }) {
  const zone = React.useMemo(() => timeZoneLabel(), []);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateTimePicker
        label={label}
        value={value ?? null}
        onChange={onChange}
        format="yyyy/MM/dd HH:mm"
        ampm={false}
        disablePast
        slotProps={{
          textField: {
            placeholder: "YYYY/MM/DD HH:MM",
            error,
            helperText: helperText || (zone ? `Your time zone: ${zone}` : undefined),
          },
        }}
      />
    </LocalizationProvider>
  );
}
